/**
 * Set selector for placeholder products bundling two real products' SKUs: single mode adds the
 * primary alone, set mode adds primary + secondary sharing a `_bundle_id` for the Cart Transform.
 */

const DIVVY_SET_ADDED_LABEL_DURATION = 3000;

/**
 * @param {number} cents
 * @returns {string}
 */
const divvySetMoney = (cents) => {
  if (typeof window.formatProductPriceNoZeros === 'function') {
    return window.formatProductPriceNoZeros(cents);
  }

  return `$${(Math.round(cents) / 100).toFixed(2)}`;
};

/**
 * @typedef {Object} DivvySetPrices
 * @property {number} price - Price charged for the current selection, in cents.
 * @property {number} compare - Value to strike through, in cents. Equal to price when not on sale.
 */

// sessionStorage key for the shopper's picked mode: a colour swatch click destroys and recreates
// this element, so it can't be held as an instance property. Panel instances only.
const DIVVY_SET_PANEL_MODE_KEY = 'divvySetPanelMode';

class DivvySetSelector extends HTMLElement {
  connectedCallback() {
    this.modeInputs = Array.from(this.querySelectorAll('input[name^="divvy-set-mode"]'));
    this.colorInputs = Array.from(this.querySelectorAll('input[name^="divvy-set-color"]'));
    this.modeLabel = this.querySelector('[data-divvy-set-mode-label]');
    this.colorLabel = this.querySelector('[data-divvy-set-color-label]');
    this.singleThumb = this.querySelector('[data-divvy-set-mode-thumb="single"]');
    this.setThumb = this.querySelector('[data-divvy-set-mode-thumb="set"]');
    this.note = this.querySelector('[data-divvy-set-note]');
    this.priceOutput = this.querySelector('[data-divvy-set-atc-price]');
    this.atcLabel = this.querySelector('[data-divvy-set-atc-label]');
    this.buyButton = this.querySelector('divvy-set-buy-button button');

    this.setHandle = this.dataset.setHandle;
    this.setVariantId = this.dataset.setVariantId;
    this.setPrice = Number(this.dataset.setPrice);
    this.primaryQuantity = Number(this.dataset.primaryQuantity) || 2;
    this.secondaryQuantity = Number(this.dataset.secondaryQuantity) || 1;
    this.addLabel = this.atcLabel ? this.atcLabel.textContent.trim() : 'Add to Bag';
    this.hideCrossOut = this.dataset.hideCrossOut === 'true';

    this.handleChange = this.handleChange.bind(this);
    this.handleGalleryUpdated = this.handleGalleryUpdated.bind(this);

    for (const input of [...this.modeInputs, ...this.colorInputs]) {
      input.addEventListener('change', this.handleChange);
    }

    this.applyModeFromUrl();
    this.restorePersistedMode();
    this.attachGalleryUpdateListeners();
    this.refresh();
  }

  disconnectedCallback() {
    for (const input of [...this.modeInputs, ...this.colorInputs]) {
      input.removeEventListener('change', this.handleChange);
    }

    for (const gallery of this.watchedGalleries || []) {
      gallery.removeEventListener('galleryUpdated', this.handleGalleryUpdated);
    }

    clearTimeout(this.labelResetTimeout);
  }

  /** Seeds a `?size=set` deep link into the same storage restorePersistedMode() reads. Panel-only. */
  applyModeFromUrl() {
    if (!this.dataset.buyButtonsSelector) return;

    if (new URLSearchParams(window.location.search).get('size') !== 'set') return;

    try {
      sessionStorage.setItem(DIVVY_SET_PANEL_MODE_KEY, 'set');
    } catch (error) {
      // Best-effort, same as persistMode().
    }
  }

  /** Restores the mode from before a colour swatch click recreated this element. Panel-only. */
  restorePersistedMode() {
    if (!this.dataset.buyButtonsSelector) return;

    let savedMode;
    try {
      savedMode = sessionStorage.getItem(DIVVY_SET_PANEL_MODE_KEY);
    } catch (error) {
      return;
    }

    if (!savedMode) return;

    // A disabled target (colour has no bundle) leaves the server-rendered default checked instead.
    const target = this.modeInputs.find((input) => input.value === savedMode);
    if (target && !target.disabled) target.checked = true;
  }

  /**
   * Rebuilds the gallery after product-info.js patches its slides post colour-swap: that patch isn't
   * built for our doubled slide count and can leave Swiper's position tracking stale.
   */
  attachGalleryUpdateListeners() {
    this.watchedGalleries = [];
    if (!this.dataset.buyButtonsSelector) return;

    for (const gallery of document.querySelectorAll(`[id^="MediaGallery-${this.dataset.sectionId}"]`)) {
      gallery.addEventListener('galleryUpdated', this.handleGalleryUpdated);
      this.watchedGalleries.push(gallery);
    }
  }

  /** @param {CustomEvent} event */
  handleGalleryUpdated(event) {
    const gallery = event.currentTarget;
    delete gallery.divvySetMedia;

    const input = this.selectedColor;
    const key = input ? this.galleryKey(input, this.mode) : null;
    if (key) this.showGalleryKey(gallery, key);
  }

  handleChange() {
    clearTimeout(this.labelResetTimeout);
    this.refresh();
  }

  /** @returns {string} Either 'single' or 'set'. */
  get mode() {
    const checked = this.modeInputs.find((input) => input.checked);
    return checked ? checked.value : 'set';
  }

  /** @returns {HTMLInputElement|null} */
  get selectedColor() {
    return this.colorInputs.find((input) => input.checked) || null;
  }

  /** @returns {boolean} Whether the current selection can be added to the cart. */
  get sellable() {
    const input = this.selectedColor;
    if (!input) return false;

    return this.isSellable(input, this.mode);
  }

  /**
   * A colour needs the primary in stock for single mode, and both pieces in stock for set mode.
   * @param {HTMLInputElement} input
   * @param {string} mode
   * @returns {boolean}
   */
  isSellable(input, mode) {
    const primaryAvailable = input.dataset.primaryAvailable === 'true';
    if (mode === 'single') return primaryAvailable;

    const secondaryAvailable = input.dataset.secondaryAvailable === 'true';
    return primaryAvailable && secondaryAvailable && input.dataset.secondaryVariantId !== '';
  }

  /** Re-evaluates swatch availability, pricing and the buy button for the current selection. */
  refresh() {
    const mode = this.mode;

    for (const input of this.colorInputs) {
      input.classList.toggle('out-of-stock', !this.isSellable(input, mode));
      this.syncSwatchImage(input, mode);
    }

    if (!this.selectedColor) {
      const firstSellable = this.colorInputs.find((input) => this.isSellable(input, mode));
      const fallback = firstSellable || this.colorInputs[0];
      if (fallback) fallback.checked = true;
    }

    const input = this.selectedColor;
    if (!input) return;

    if (this.modeLabel) {
      this.modeLabel.textContent = mode === 'single' ? this.dataset.singleLabel : this.dataset.setLabel;
    }

    if (this.colorLabel) {
      this.colorLabel.textContent = input.dataset.colorName;
    }

    this.updateThumbSrc(this.singleThumb, input.dataset.primaryImage);
    this.updateThumbSrc(this.setThumb, input.dataset.setImage);

    this.updateNote(input, mode);
    this.updatePrices(this.prices(input, mode));
    this.updateBuyButton();
    this.syncGallery(input, mode);
    this.syncBuyButtons();
    this.syncAccordions();
    this.syncExternalSwatches(mode);
    this.syncTitle(input, mode);
    this.persistMode(mode);
  }

  /**
   * Swaps the product title to match the selection: this colour's own title in single mode, the
   * linked placeholder's in set mode. Plain text only (unlike syncAccordions(), which handles the
   * short description's richer content via pre-rendered twin bodies instead).
   * @param {HTMLInputElement} input
   * @param {string} mode
   */
  syncTitle(input, mode) {
    const title = mode === 'single' ? input.dataset.singleTitle : this.dataset.setTitle;
    if (!title) return;

    for (const el of document.querySelectorAll('[data-divvy-set-title]')) {
      el.textContent = title;
    }
  }

  /** Persists the mode across a colour swatch click. @param {string} mode */
  persistMode(mode) {
    if (!this.dataset.buyButtonsSelector) return;

    try {
      sessionStorage.setItem(DIVVY_SET_PANEL_MODE_KEY, mode);
    } catch (error) {
      // Best-effort; ignore storage errors in privacy modes.
    }
  }

  /** Swaps the theme's own colour swatches to their set-mode imagery. Panel-only. */
  syncExternalSwatches(mode) {
    if (!this.dataset.buyButtonsSelector) return;

    for (const img of document.querySelectorAll('associated-variant-radios img[data-swatch-set-image]')) {
      const src = mode === 'set' ? img.dataset.swatchSetImage : img.dataset.swatchSingleImage;
      this.updateThumbSrc(img, src);
    }
  }

  /** Toggles the standard buy button vs. this element's bundle button by mode. Panel-only. */
  syncBuyButtons() {
    const selector = this.dataset.buyButtonsSelector;
    if (!selector) return;

    const standardWrapper = document.querySelector(selector)?.closest('.product-form__wrapper');
    const bundleWrapper = this.querySelector('.divvy-set__atc');
    const showBundle = this.mode === 'set';

    standardWrapper?.classList.toggle('hidden', showBundle);
    bundleWrapper?.classList.toggle('hidden', !showBundle);
  }

  /** Shows the matching accordion body per mode; hides the row entirely if no Set-mode copy. */
  syncAccordions() {
    const mode = this.mode;

    for (const row of document.querySelectorAll('[data-divvy-set-tab]')) {
      const bodies = row.querySelectorAll('[data-divvy-set-tab-body]');
      const activeBody = row.querySelector(`[data-divvy-set-tab-body="${mode}"]`);

      row.classList.toggle('hidden', !activeBody);
      for (const body of bodies) {
        body.classList.toggle('hidden', body !== activeBody);
      }
    }
  }

  /** Points one swatch's thumbnail at its single- or set-mode image. */
  syncSwatchImage(input, mode) {
    const label = this.querySelector(`label[for="${input.id}"]`);
    const thumb = label?.querySelector('[data-divvy-set-color-thumb]');
    const src = mode === 'set' ? input.dataset.swatchSetImage : input.dataset.swatchSingleImage;

    this.updateThumbSrc(thumb, src);
  }

  /** Compares on the attribute, not the property, since `image_url` reads back differently. */
  updateThumbSrc(img, src) {
    if (!img || !src || img.getAttribute('src') === src) return;

    img.setAttribute('src', src);
  }

  /** The data-color key for this selection: colour handle, plus '--set' in set mode. */
  galleryKey(input, mode) {
    const color = input.dataset.colorHandle;
    if (!color) return null;

    const showSetImages = mode === 'set' && input.dataset.hasSetImages === 'true';
    return showSetImages ? `${color}--set` : color;
  }

  /** Points the gallery and zoom modal at the images for the current selection. */
  syncGallery(input, mode) {
    const key = this.galleryKey(input, mode);
    if (!key) return;

    for (const gallery of document.querySelectorAll(`[id^="MediaGallery-${this.dataset.sectionId}"]`)) {
      this.showGalleryKey(gallery, key);
    }

    this.filterModal(key);
  }

  /**
   * Swaps the gallery to one colour/mode. Slides are removed from the wrapper, not just hidden,
   * since Swiper sizes off the slides it owns and a hidden one still holds a position. Off-screen
   * colours are kept in a per-key map and swapped back in later; detached images never load.
   */
  showGalleryKey(gallery, key) {
    const wrapper = gallery.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const media = this.galleryMedia(gallery, wrapper);
    const slides = media.slides.get(key) || media.slides.get(key.replace(/--set$/, ''));
    if (!slides || slides.length === 0) return;

    const thumbs = media.thumbs.get(key) || media.thumbs.get(key.replace(/--set$/, '')) || [];

    // Rebuilt rather than updated: Swiper's loop mode owns slide order, so new children need a fresh instance.
    const hadSlider = Boolean(gallery.slider);
    if (hadSlider) {
      gallery.slider.destroy(true, true);
      gallery.slider = null;
    }

    wrapper.replaceChildren(...slides);
    slides.forEach((slide, index) => {
      slide.classList.remove('is-hidden');
      slide.classList.toggle('is-active', index === 0);
    });

    if (media.thumbList) {
      media.thumbList.replaceChildren(...thumbs);
      thumbs.forEach((thumb, index) => {
        thumb.classList.remove('is-hidden');
        const button = thumb.querySelector('button');
        if (!button) return;

        if (index === 0) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      // Strip layout/arrows are sized from the colour on load; colours don't all have the same count.
      const strip = gallery.querySelector('[id^="GalleryThumbnails"]');
      strip?.classList.toggle('thumbnail-slider--no-slide', thumbs.length <= 3);

      for (const button of strip?.querySelectorAll('.slider-button') || []) {
        button.classList.toggle('small-hide', thumbs.length <= 3);
        button.classList.toggle('medium-hide', thumbs.length <= 4);
        button.classList.toggle('large-up-hide', thumbs.length <= 4);
      }
    }

    // Set before the event so an unupgraded gallery reads it in its own connectedCallback.
    gallery.dataset.activeColor = key;
    gallery.dataset.totalMediaCount = String(slides.length);
    gallery.dispatchEvent(new CustomEvent('colorChanged', { detail: { color: key } }));

    if (hadSlider) gallery.initSlider?.();
  }

  /** Groups slides/thumbnails by data-color, once per gallery, before any JS reorders them. */
  galleryMedia(gallery, wrapper) {
    if (gallery.divvySetMedia) return gallery.divvySetMedia;

    const groupByColor = (nodes) => {
      const grouped = new Map();

      for (const node of nodes) {
        const nodeKey = node.dataset.color;
        if (!nodeKey) continue;

        if (!grouped.has(nodeKey)) grouped.set(nodeKey, []);
        grouped.get(nodeKey).push(node);
      }

      return grouped;
    };

    const thumbList = gallery.querySelector('.thumbnail-list');

    gallery.divvySetMedia = {
      thumbList,
      slides: groupByColor(wrapper.querySelectorAll(':scope > li[data-color]')),
      thumbs: groupByColor(thumbList ? thumbList.querySelectorAll(':scope > li[data-color]') : [])
    };

    return gallery.divvySetMedia;
  }

  /** @param {string} key */
  filterModal(key) {
    const modal = document.getElementById(`ProductModal-${this.dataset.sectionId}`);
    if (!modal) return;

    for (const item of modal.querySelectorAll('[data-color]')) {
      item.classList.toggle('divvy-set-modal__item--hidden', item.dataset.color !== key);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} mode
   * @returns {DivvySetPrices}
   */
  prices(input, mode) {
    if (mode === 'single') {
      return {
        price: Number(input.dataset.primaryPrice),
        compare: Number(input.dataset.primaryComparePrice)
      };
    }

    return {
      price: this.setPrice,
      compare: Number(input.dataset.setValue)
    };
  }

  /**
   * Explains why a colour cannot be added, so the disabled button is never unexplained.
   * @param {HTMLInputElement} input
   * @param {string} mode
   */
  updateNote(input, mode) {
    if (!this.note) return;

    const primaryAvailable = input.dataset.primaryAvailable === 'true';
    const hasSecondary = input.dataset.secondaryVariantId !== '';
    const secondaryAvailable = input.dataset.secondaryAvailable === 'true';

    if (!primaryAvailable) {
      this.note.textContent = this.dataset.noteSoldOut;
      return;
    }

    if (mode === 'single') {
      this.note.textContent = '';
      return;
    }

    if (!hasSecondary) {
      this.note.textContent = this.dataset.noteSingleOnly;
      return;
    }

    this.note.textContent = secondaryAvailable ? '' : this.dataset.noteSoldOutAsSet;
  }

  /**
   * The PDP renders the price twice: once in the title block and once in the price block.
   * @returns {HTMLElement[]}
   */
  get priceWrappers() {
    const inTitle = Array.from(document.querySelectorAll('.product__title > .price'));
    const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
    const inBlock = priceBlock ? Array.from(priceBlock.querySelectorAll('.price')) : [];

    return [...inTitle, ...inBlock];
  }

  /**
   * Updates the product price displays and the price shown inside the buy button.
   * @param {DivvySetPrices} prices
   */
  updatePrices({ price, compare }) {
    const onSale = this.hideCrossOut ? false : compare > price;

    for (const priceWrapper of this.priceWrappers) {
      const regular = priceWrapper.querySelector('.price__regular .price-item--regular');
      const saleCompare = priceWrapper.querySelector('.price__sale s.price-item--regular');
      const sale = priceWrapper.querySelector('.price__sale .price-item--sale');

      priceWrapper.classList.toggle('price--on-sale', onSale);

      if (onSale && saleCompare && sale) {
        saleCompare.textContent = divvySetMoney(compare);
        sale.textContent = divvySetMoney(price);
      } else if (regular) {
        regular.textContent = divvySetMoney(price);
      }
    }

    if (!this.priceOutput) return;

    this.priceOutput.textContent = onSale ? `${divvySetMoney(price)} ` : divvySetMoney(price);

    if (!onSale) return;

    const valueLabel = document.createElement('span');
    valueLabel.textContent = (this.dataset.valueLabel || '(__price__ Value)').replace(
      '__price__',
      divvySetMoney(compare)
    );
    this.priceOutput.appendChild(valueLabel);
  }

  /** Locks the buy button when the selection is not purchasable. */
  updateBuyButton() {
    if (!this.buyButton) return;

    const sellable = this.sellable;

    this.buyButton.disabled = !sellable;
    this.buyButton.setAttribute('aria-disabled', String(!sellable));

    if (this.atcLabel) {
      this.atcLabel.textContent = sellable ? this.addLabel : this.dataset.soldOutLabel;
    }

    if (this.priceOutput) {
      this.priceOutput.hidden = !sellable;
    }
  }

  /**
   * Shows transient feedback on the button, then restores its normal state.
   * @param {string} label
   */
  flashLabel(label) {
    if (!this.atcLabel) return;

    this.atcLabel.textContent = label;
    if (this.priceOutput) this.priceOutput.hidden = true;

    this.labelResetTimeout = setTimeout(() => this.refresh(), DIVVY_SET_ADDED_LABEL_DURATION);
  }

  /** Puts a cart error in the note, a live region, so it's announced, not just implied by the label. */
  showError(error) {
    if (!this.note || typeof error !== 'string' || error === '') return;

    this.note.textContent = error;
  }

  /**
   * Builds the `/cart/add.js` payload for the current selection.
   * @returns {Array<Object>|null} Line items, or null when nothing is purchasable.
   */
  buildCartItems() {
    const input = this.selectedColor;
    if (!input) return null;

    const mode = this.mode;
    if (!this.isSellable(input, mode)) return null;

    const primaryVariantId = input.dataset.primaryVariantId;

    if (mode === 'single') {
      return [{ id: primaryVariantId, quantity: 1 }];
    }

    const componentValue = Number(input.dataset.setValue);
    const discountPercentage =
      componentValue > this.setPrice
        ? Math.round(((componentValue - this.setPrice) / componentValue) * 100)
        : 0;

    // Shared by the whole add so the Cart Transform can group these lines.
    const bundleId = `${Number(this.setVariantId)}|${Date.now()}`;
    const variantIds = [
      ...Array(this.primaryQuantity).fill(primaryVariantId),
      ...Array(this.secondaryQuantity).fill(input.dataset.secondaryVariantId)
    ];

    // Quantity-1 lines so the function sees the same shape as bundles.
    return variantIds.map((id, index) => ({
      id,
      quantity: 1,
      properties: {
        _id: `${bundleId}-${index}`,
        _bundle_handle: this.setHandle,
        _bundle_id: bundleId,
        _bundle_variant_id: Number(this.setVariantId),
        _bundle_items: variantIds.join('|'),
        _bundle_price: componentValue,
        _bundle_discount_percentage: discountPercentage,
        _bundle_final_price: this.setPrice,
        // Read by the Cart Transform function (attribute_5) to set the merged line's own image,
        // since its parentVariantId is this set's colourless placeholder variant. The set-mode
        // image (custom.set_gallery_images' first entry on the primary, already resolved into
        // this same attribute for the mode thumbnail/gallery card), not the single-panel image,
        // since this only ever runs for the set-mode add.
        _bundle_image: input.dataset.setImage
      }
    }));
  }
}

customElements.define('divvy-set-selector', DivvySetSelector);

class DivvySetBuyButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.spinner = this.querySelector('.loading-overlay__spinner');
    this.loading = false;
    this.handleClick = this.handleClick.bind(this);

    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  async handleClick(event) {
    event.preventDefault();

    if (this.loading) return;

    const selector = this.closest('divvy-set-selector');
    if (!selector) return;

    const items = selector.buildCartItems();
    if (!items) return;

    if (typeof window.theme?.addToCart !== 'function') {
      console.error('Divvy set add to cart error: cart store is not available');
      return;
    }

    this.setLoading(true);

    try {
      const result = await window.theme.addToCart(items);

      if (result && result.error) {
        selector.showError(result.error);
        selector.flashLabel(selector.dataset.retryLabel);
        return;
      }

      selector.flashLabel(selector.dataset.addedLabel);
    } catch (error) {
      console.error('Divvy set add to cart error:', error);
      selector.flashLabel(selector.dataset.retryLabel);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * @param {boolean} loading
   */
  setLoading(loading) {
    this.loading = loading;

    if (!this.button) return;

    this.button.classList.toggle('loading', loading);
    this.spinner?.classList.toggle('hidden', !loading);

    if (loading) {
      this.button.disabled = true;
      this.button.setAttribute('aria-disabled', 'true');
      return;
    }

    // Restores the selection's own lock state rather than just enabling.
    const sellable = this.closest('divvy-set-selector')?.sellable ?? true;

    this.button.disabled = !sellable;
    this.button.setAttribute('aria-disabled', String(!sellable));
  }
}

customElements.define('divvy-set-buy-button', DivvySetBuyButton);
